import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Store, Phone, ShieldCheck, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function VendorLoginPage() {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  const otpInputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithOTP, isAuthenticated, isApproved, isPending } = useVendor();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const justRegistered = location.state?.registered;

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/vendor/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (step === 2 && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, [step]);

  const handleSendOTP = async () => {
    if (phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    setLoading(true);
    try {
      const formattedPhone = '+91' + phone.replace(/\D/g, '');
      
      await axios.post(`${API_URL}/api/vendor/send-otp`, { phone: formattedPhone });
      toast.success('OTP sent to your phone');
      setStep(2);
      setResendTimer(30);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send OTP';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
    
    if (value && index === 5) {
      const fullOtp = newOtp.join('');
      if (fullOtp.length === 6) {
        handleVerifyOTP(fullOtp);
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async (otpCode) => {
    const code = otpCode || otp.join('');
    if (code.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const formattedPhone = '+91' + phone.replace(/\D/g, '');
      const response = await axios.post(`${API_URL}/api/vendor/verify-otp`, {
        phone: formattedPhone,
        code: code
      });
      
      toast.success('Login successful!');
      loginWithOTP(response.data.access_token, response.data.vendor);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="flex items-center justify-center space-x-3 mb-4">
            <Store className="w-10 h-10 text-green-700" />
            <span className="text-2xl font-bold text-green-800">nammahut vendor</span>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Vendor Login</h1>
          <p className="text-gray-600">Access your seller dashboard</p>
        </div>

        {/* Registration Success Message */}
        {justRegistered && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-green-900">Registration Submitted!</h4>
              <p className="text-sm text-green-700">Your application is pending admin approval. You'll be notified once approved.</p>
            </div>
          </div>
        )}

        <Card className="border-2 border-green-100 shadow-xl">
          {step === 1 ? (
            <>
              <CardHeader className="text-center bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <CardTitle>Enter Phone Number</CardTitle>
                <CardDescription>Use your registered phone number</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex">
                    <div className="flex items-center px-4 bg-gray-100 border-2 border-r-0 border-green-200 rounded-l-lg">
                      <span className="text-gray-600 font-medium">+91</span>
                    </div>
                    <Input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="flex-1 border-2 border-green-200 rounded-l-none text-lg"
                      placeholder="Enter phone number"
                      maxLength={10}
                      data-testid="vendor-phone-input"
                    />
                  </div>
                  
                  <Button 
                    onClick={handleSendOTP} 
                    className="w-full btn-primary py-6"
                    disabled={loading || phone.length < 10}
                    data-testid="vendor-send-otp-btn"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    Get OTP
                  </Button>
                </div>
                
                <p className="text-center text-sm text-gray-500 mt-6">
                  New to nammahut?{' '}
                  <Link to="/vendor/register" className="text-green-600 font-medium hover:underline">
                    Register as Seller
                  </Link>
                </p>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="text-center bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
                <CardTitle>Verify OTP</CardTitle>
                <CardDescription>Sent to +91 {phone}</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex justify-center gap-2 mb-6">
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => (otpInputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 border-green-200"
                      data-testid={`vendor-otp-input-${index}`}
                    />
                  ))}
                </div>
                
                <Button 
                  onClick={() => handleVerifyOTP()}
                  className="w-full btn-primary py-6"
                  disabled={loading || otp.join('').length !== 6}
                  data-testid="vendor-verify-otp-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                  Verify & Login
                </Button>
                
                <p className="text-center text-sm text-gray-500 mt-4">
                  Didn't receive OTP?{' '}
                  {resendTimer > 0 ? (
                    <span>Resend in {resendTimer}s</span>
                  ) : (
                    <button onClick={handleSendOTP} className="text-green-600 font-medium hover:underline">
                      Resend OTP
                    </button>
                  )}
                </p>
                
                <button 
                  onClick={() => { setStep(1); setOtp(['', '', '', '', '', '']); }}
                  className="w-full text-center text-sm text-gray-500 mt-4 hover:text-green-600"
                >
                  Change phone number
                </button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
